import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Upload, Link, Save, Eye, X, Check, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";

interface Store {
  id: string;
  name: string;
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  stock?: number; // Optional for existing products
  category: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
  store_id?: string;
  stores?: { name: string };
}

interface ProductFormProps {
  productToEdit?: Product | null;
  onProductAdded: () => void;
  onClose: () => void;
}

const ProductForm = ({ productToEdit, onProductAdded, onClose }: ProductFormProps) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    price: "",
    stock: "",
    category: "",
    imageUrl: "",
    is_active: true,
    storeId: "",
  });
  const [stores, setStores] = useState<Store[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [imageTab, setImageTab] = useState("upload");
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchStores = async () => {
    try {
      const { data, error } = await supabase
        .from('stores')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      setStores(data || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  useEffect(() => {
    fetchStores();
    
    if (productToEdit) {
      setFormData({
        name: productToEdit.name,
        description: productToEdit.description || "",
        price: productToEdit.price.toString(),
        stock: productToEdit.stock?.toString() || "",
        category: productToEdit.category,
        is_active: productToEdit.is_active,
        imageUrl: productToEdit.image_url || "",
        storeId: productToEdit.store_id || "",
      });
      setImagePreview(productToEdit.image_url || "");
    }
  }, [productToEdit]);

  const { toast } = useToast();
  const { user, userProfile } = useAuth();

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: "Unsupported File Format",
          description: "Only PNG, JPG, JPEG, and WebP formats are supported",
          variant: "destructive",
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Maximum file size is 5MB",
          variant: "destructive",
        });
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUrlChange = (url: string) => {
    setFormData(prev => ({ ...prev, imageUrl: url }));
    setImagePreview(url);
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${user?.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('product-images')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('product-images')
      .getPublicUrl(fileName);

    return data.publicUrl;
  };

  const validateForm = () => {
    if (!formData.name) {
      toast({
        title: "Missing Required Field",
        description: "Product name is required",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.price || isNaN(parseFloat(formData.price))) {
      toast({
        title: "Invalid Price",
        description: "Please enter a valid price",
        variant: "destructive",
      });
      return false;
    }

    if (!formData.category) {
      toast({
        title: "Missing Required Field",
        description: "Product category is required",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !userProfile) {
      toast({
        title: "Authentication Error",
        description: "Please log in to continue",
        variant: "destructive",
      });
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    setUploadProgress(0);

    try {
      let finalImageUrl = formData.imageUrl;

      // Upload image file if provided
      if (imageFile && imageTab === "upload") {
        finalImageUrl = await uploadImage(imageFile);
        setUploadProgress(100);
      }

      // Upsert product
      const { error } = await supabase.from('products').upsert({
        ...(productToEdit?.id && { id: productToEdit.id }),
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        stock: formData.stock ? parseInt(formData.stock) : 0,
        category: formData.category as "makanan" | "minuman" | "merchandise" | "kerajinan",
        image_url: finalImageUrl,
        store_id: formData.storeId || userProfile.store_id || null,
        created_by: user.id,
        is_active: formData.is_active,
      });

      if (error) throw error;

      toast({
        title: productToEdit ? "Product Updated" : "Product Added",
        description: productToEdit 
          ? "Your product changes have been saved" 
          : "The product has been added to your catalog",
      });

      // Reset form
      if (!productToEdit) {
        setFormData({
          name: "",
          description: "",
          price: "",
          stock: "",
          category: "",
          imageUrl: "",
          is_active: true,
          storeId: "",
        });
        setImageFile(null);
        setImagePreview("");
      }

      onProductAdded();
      onClose();

    } catch (error: any) {
      console.error('Error saving product:', error);
      toast({
        title: "Error Saving Product",
        description: error.message || "An error occurred while saving the product",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  return (
      <Card className="w-full max-w-3xl">
        <CardHeader className="border-b">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-xl">
                {productToEdit ? "Edit Product" : "Add New Product"}
              </CardTitle>
              <CardDescription>
                {productToEdit 
                  ? "Update your product details below"
                  : "Fill in the product information to add to your catalog"}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Product Status */}
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div>
                    <Label className="font-medium">Product Status</Label>
                    <p className="text-sm text-muted-foreground">
                      {formData.is_active ? "Active and visible" : "Inactive and hidden"}
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => 
                      setFormData(prev => ({ ...prev, is_active: checked }))
                    }
                  />
                </div>

                {/* Product Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g. Premium Coffee Beans"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>

                {/* Price */}
                <div className="space-y-2">
                  <Label htmlFor="price">Price *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-muted-foreground">Rp</span>
                    <Input
                      id="price"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.price}
                      onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Stock Product */}
                <div className="space-y-2">
                  <Label htmlFor="stock">Stock *</Label>
                  <Input
                    id="stock"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.stock}
                    onChange={(e) => setFormData(prev => ({ ...prev, stock: e.target.value }))}
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="makanan">Food</SelectItem>
                      <SelectItem value="minuman">Beverage</SelectItem>
                      <SelectItem value="merchandise">Merchandise</SelectItem>
                      <SelectItem value="kerajinan">Handicraft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Store */}
                <div className="space-y-2">
                  <Label htmlFor="store">Store *</Label>
                  <Select
                    value={formData.storeId}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, storeId: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a store" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map((store) => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Product details, features, etc."
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                  />
                </div>

                {/* Image Upload */}
                <div className="space-y-2">
                  <Label>Product Image</Label>
                  <Tabs value={imageTab} onValueChange={setImageTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="upload">Upload</TabsTrigger>
                      <TabsTrigger value="url">Image URL</TabsTrigger>
                    </TabsList>

                    <TabsContent value="upload" className="space-y-3">
                      <div className="flex flex-col space-y-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Choose File
                        </Button>
                        {imageFile && (
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Check className="w-4 h-4 mr-2 text-green-500" />
                            {imageFile.name}
                          </div>
                        )}
                        {!imageFile && (
                          <p className="text-sm text-muted-foreground">
                            PNG, JPG, JPEG, WebP (max 5MB)
                          </p>
                        )}
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={handleImageFileChange}
                        className="hidden"
                      />
                    </TabsContent>

                    <TabsContent value="url" className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Link className="w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="https://example.com/image.jpg"
                          value={formData.imageUrl}
                          onChange={(e) => handleImageUrlChange(e.target.value)}
                        />
                      </div>
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            </div>

            {/* Image Preview */}
            {imagePreview && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <Eye className="w-4 h-4" />
                  <Label>Image Preview</Label>
                </div>
                <div className="flex justify-center">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full max-w-xs h-48 object-contain rounded-lg"
                    onError={() => {
                      setImagePreview("");
                      toast({
                        title: "Image Error",
                        description: "Failed to load the image",
                        variant: "destructive",
                      });
                    }}
                  />
                </div>
              </div>
            )}

            {/* Progress Bar */}
            {uploadProgress !== null && (
              <div className="space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-300" 
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="min-w-[150px]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {productToEdit ? "Save Changes" : "Add Product"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
  );
};

export default ProductForm;